import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { isEmpty, omit, orderBy, map } from 'lodash';

import { displayDate, displayMonitoringYear } from 'utils/dateUtil';
import { useSearchInstrumentsQuery, useSearchInstrumentDraftsQuery, instrumentsApi } from 'app/apis/instruments.api';

import { searchParamsToApiParams, formToSearchParams, searchParamsToForm } from './search-utils';

const SearchContext = createContext({});

export const SearchProvider = ({ children }) => {
  const [selected, setSelected] = useState([]);
  const [sortInfo, setSortInfo] = useState(null);
  const [forceExecute, setForceExecute] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { lang } = useParams();

  const searchMode = searchParams.get('mode');
  const isAdvancedSearch = searchMode === 'advanced';
  const isPublishSearch = searchMode === 'publish';
  const publishStatus = searchParams.get('publishStatus');
  const shouldFetchDratfs = isPublishSearch && publishStatus === 'notPublished';

  const apiQuery = searchParamsToApiParams(searchParams, lang);
  const apiQueryHasParams = !isEmpty(omit(apiQuery, ['lang', 'mode']));
  const shouldFetchInstruments = (!shouldFetchDratfs && apiQueryHasParams) || forceExecute;

  const {
    data: instrumentsData,
    isFetching: isFetchingInstruments,
    error: instrumentsSearchError,
  } = useSearchInstrumentsQuery(apiQuery, {
    skip: !shouldFetchInstruments,
  });

  const {
    data: draftsData,
    isFetching: isFetchingDrafts,
    error: draftsSearchError,
  } = useSearchInstrumentDraftsQuery(apiQuery, {
    skip: !shouldFetchDratfs
  })


  const searchFormDefaults = {
    statusIds: [],
    typeIds: [],
    committeeIds: [],
    themeIds: [],
    adherentIds: [],
    reference: '',
    term: '',
    yearFrom: null,
    yearTo: null,
  };
  const searchForm = useForm({
    defaultValues: searchFormDefaults,
  });

  useEffect(() => {
    if (searchMode === 'normal' && !searchParams.has('statusIds')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('statusIds', '1')
      setSearchParams(newSearchParams);
    } else if (isPublishSearch && !searchParams.has('publishStatus')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('publishStatus', 'notPublished')
      setSearchParams(newSearchParams);
    }
    return async () => {
    };
  }, [searchMode]);

  useEffect(() => {
    searchForm.reset({
      ...searchFormDefaults,
      ...searchParamsToForm(searchParams),
    });
  }, [searchParams]);

  const applySearch = () => {
    setSortInfo(null);
    const nextSearchParams = new URLSearchParams(formToSearchParams(searchForm.getValues()));
    nextSearchParams.set('mode', searchMode);
    setSearchParams(nextSearchParams);
    setForceExecute(true);
  };
  const clearFilters = () => {
    searchForm.reset(searchFormDefaults);
  };

  const listData = shouldFetchDratfs
    ? draftsData
    : instrumentsData;

  let dateTypeParam = searchParams.get('dateType');
  const showLastAmendmentDate = dateTypeParam === 'lastAmendment';
  const showEntryInForceDate = dateTypeParam === 'inForce';
  const showMonitoringDate = dateTypeParam === 'monitoring';
  const showAbrogationDate = dateTypeParam === 'abrogation' || (searchParams.get('statusIds') === '2' && !searchParams.has('dateType'));
  const showAdoptionDate = true;

  const memoizedListData = React.useMemo(() => {
    const uiMappedListData = map(listData, item => {
      const uiItem = { ...item };
      if (uiItem.adoptionDate) {
        uiItem.adoptionDate_display = displayDate(uiItem.adoptionDate)
      }
      if (uiItem.abrogationDate) {
        uiItem.abrogationDate_display = displayDate(uiItem.abrogationDate)
      }
      if (uiItem.inForceDate) {
        uiItem.inForceDate_display = displayDate(uiItem.inForceDate)
      }
      if (uiItem.lastAmendmentDate) {
        uiItem.lastAmendmentDate_display = displayDate(uiItem.lastAmendmentDate)
      }
      if(uiItem.monitoring) {
        uiItem.monitoringDate_display = displayMonitoringYear(uiItem.monitoring)
      }

      uiItem.inForceDate_date = uiItem.inForceDate ? Date.parse(uiItem.inForceDate) : 0;
      uiItem.lastAmendmentDate_date = uiItem.lastAmendmentDate ? Date.parse(uiItem.lastAmendmentDate) : 0;
      uiItem.adoptionDate_date = uiItem.adoptionDate ? Date.parse(uiItem.adoptionDate) : 0;
      uiItem.abrogationDate_date = uiItem.abrogationDate ? Date.parse(uiItem.abrogationDate) : 0;
      uiItem.monitoringDate_ordering = uiItem.monitoringDate_display ? uiItem.monitoringDate_display : '';

      return uiItem;
    });

    if (searchParams.has('term') && searchParams.get('searchScope') === 'fullText') {
      return uiMappedListData;
    }

    if (sortInfo?.orderBy) {
      return orderBy(uiMappedListData, [sortInfo.orderBy], [sortInfo.order || 'asc']);
    }

    if (showEntryInForceDate) {
      return orderBy(uiMappedListData, ['inForceDate_date', 'adoptionDate_date'], ['desc', 'desc']);
    }

    if (showLastAmendmentDate) {
      return orderBy(uiMappedListData, ['lastAmendmentDate_date', 'adoptionDate_date'], ['desc', 'desc']);
    }

    if(showAbrogationDate) {
      return orderBy(uiMappedListData, ['abrogationDate_date', 'adoptionDate_date'], ['desc', 'desc']);
    }

    if(showMonitoringDate) {
      return orderBy(uiMappedListData, ['monitoringDate_ordering', 'adoptionDate_date'], ['desc', 'desc']);
    }

    return orderBy(uiMappedListData, ['status.id', 'adoptionDate_date'], ['asc', 'desc']);
  }, [listData, sortInfo]);

  const onSortChange = (field) => {
    setSortInfo(currentSortInfo => {
      let nextOrder = currentSortInfo?.order === 'asc' ? 'desc' : 'asc';
      if (currentSortInfo?.orderBy !== field) {
        nextOrder = 'asc';
      }
      return {
        orderBy: field,
        order: nextOrder,
      };
    });
  };

  const onSearchFormSubmit = searchForm.handleSubmit(applySearch);

  const value = {
    instruments: instrumentsData, drafts: draftsData, listData, applySearch, clearFilters, onSearchFormSubmit,
    isSearchLoading: isFetchingDrafts || isFetchingInstruments,
    isAdvancedSearch, isPublishSearch,
    selected, setSelected, memoizedListData,
    showAdoptionDate, showAbrogationDate, showEntryInForceDate, showLastAmendmentDate, showMonitoringDate,
    sortInfo, onSortChange, shouldFetchInstruments,
  };

  return (
    <SearchContext.Provider value={value}>
      <FormProvider {...searchForm}>
        {children}
      </FormProvider>
    </SearchContext.Provider>
  );
};

export const useSearch = () => useContext(SearchContext);
